import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, Input, Output, ViewChild, NgModule } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import * as i1 from '@azavista/components/shared';
import { AzavistaSharedModule } from '@azavista/components/shared';
import * as i3 from '@angular/common';
import * as i4 from '@ngx-translate/core';
import * as i5 from '@angular/material/expansion';
import { MatExpansionModule } from '@angular/material/expansion';
import * as i6 from '@azavista/components/input-field';
import { AzavistaInputFieldModule } from '@azavista/components/input-field';
import * as i7 from '@angular/material/form-field';
import * as i8 from '@azavista/components/button';
import { AzavistaButtonModule } from '@azavista/components/button';
import * as i9 from '@azavista/components/selected-items-count';
import { AzavistaSelectedItemsCountModule } from '@azavista/components/selected-items-count';
import { MatDividerModule } from '@angular/material/divider';

class AzavistaGroupedFieldsService {
    constructor(sharedSvc) {
        this.sharedSvc = sharedSvc;
    }
    sortGroups(groups, singleColumn) {
        groups.sort((left, right) => left.key.localeCompare(right.key));
        for (const group of groups) {
            group.items.sort((left, right) => left.field.label.localeCompare(right.field.label));
            if (!singleColumn) {
                // Two columns must show items by columns - when the first colulmn shows the half of the fields,
                // the rest should be shown on the second column
                // So instead of the followig natural CSS grid ordering with two columns:
                // Field1    Field2
                // Field3    Field4
                // Field5
                // In an array - [Field1, Field2, Field3, Field4, Field5]
                // We need to order them by columns like:
                // Field1    Field4
                // Field2    Field5
                // Field3
                // in an array - [Field1, Field4, Field2, Field5, Field3]
                group.items = this.orderItemsByTwoColumns(group.items);
            }
        }
    }
    orderItemsByTwoColumns(items) {
        const ordered = [...items];
        const result = [];
        const itemsInColumn = Math.ceil(items.length / 2);
        for (let i = 0; i < itemsInColumn; i++) {
            result.push(ordered[i]);
            const secondColumnIndex = i + itemsInColumn;
            if (secondColumnIndex < ordered.length) {
                result.push(ordered[secondColumnIndex]);
            }
        }
        return result;
    }
    getFieldWithValueInGroups(fieldId, groups) {
        for (const group of groups) {
            const fieldWithValue = group.items.find(x => x.field.id === fieldId);
            if (fieldWithValue) {
                return fieldWithValue;
            }
        }
        return null;
    }
    getObjectEnumOriginalValues(changesOnlyObject, fieldsWithEntity) {
        if (!fieldsWithEntity.fields || !changesOnlyObject) {
            return {};
        }
        const changedObjectProps = Object.keys(changesOnlyObject);
        const changedObjectEnumFields = fieldsWithEntity.fields.filter(x => this.sharedSvc.getFieldTypeFromField(x) === 'virtual-object_enum'
            && changedObjectProps.find(y => y === x.name));
        // Set all object_enum field values in entity "selected" to the item with new reported property
        const objectEnumFieldValues = {};
        for (const changedObjectEnumField of changedObjectEnumFields) {
            const fieldName = changedObjectEnumField.name;
            const entityValue = fieldsWithEntity.entity[fieldName];
            if (entityValue && entityValue.items) {
                const reportProp = entityValue.reportProperty;
                if (reportProp) {
                    const changedFieldValue = changesOnlyObject[fieldName];
                    entityValue.selected = entityValue.items.find(x => x[reportProp] === changedFieldValue);
                    objectEnumFieldValues[fieldName] = entityValue;
                }
            }
        }
        return objectEnumFieldValues;
    }
    createObjectFromChanges(changes) {
        if (!changes || changes.length === 0) {
            return null;
        }
        const result = {};
        for (const change of changes) {
            result[change.field.name] = change.value;
        }
        return result;
    }
    addChange(fieldWithValue, change, changes) {
        // const areValuesEqual = this.sharedSvc.areValuesEqual(change.originalValue, change.value);
        const existingIndex = changes.findIndex(x => x.field === fieldWithValue.field);
        // if (areValuesEqual) {
        //     if (existingIndex >= 0) {
        //         // The values are equal and we have that item (probably it was changed and now changed back to original)
        //         // Remove the item
        //         changes.splice(existingIndex, 1);
        //     }
        //     // In any case if the values are equal, we do not add new or change existing
        //     return;
        // }
        if (existingIndex >= 0) {
            // This field already exists - change it
            changes[existingIndex] = change;
        }
        else {
            // This field does not exist in the list - add it
            changes.push(change);
        }
    }
    setError(field, changes) {
        const existingIndx = changes.findIndex(x => x.field === field);
        if (existingIndx >= 0) {
            // The field with error is found in changes - remove it since error value is not considered a valid change
            changes.splice(existingIndx, 1);
        }
    }
    createGroups(groupedFields, entity, fieldsOptions) {
        const result = [];
        for (const grp of groupedFields) {
            result.push({
                key: grp.key,
                items: this.createGroupItems(grp.items, entity, fieldsOptions)
            });
        }
        return result;
    }
    createGroupItems(fields, entity, fieldsOptions) {
        const result = [];
        for (const field of fields) {
            const fieldWithValue = {
                field: field, value: entity[field.name]
            };
            if (fieldsOptions) {
                const fieldOptions = fieldsOptions.find(x => x.fieldId === field.id);
                if (fieldOptions) {
                    fieldWithValue.options = fieldOptions.options;
                }
            }
            result.push(fieldWithValue);
        }
        return result;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaGroupedFieldsService, deps: [{ token: i1.AzavistaSharedService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaGroupedFieldsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaGroupedFieldsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.AzavistaSharedService }]; } });

class AzavistaGroupedFieldsComponent {
    constructor(sharedSvc, grpFldsSvc, cdr, ngZone) {
        this.sharedSvc = sharedSvc;
        this.grpFldsSvc = grpFldsSvc;
        this.cdr = cdr;
        this.ngZone = ngZone;
        this.actionButtonsDisabled = false;
        this.progressText = '';
        this.floatActions = true;
        this.valuesChanged = new EventEmitter();
        this.cancelled = new EventEmitter();
        this.saved = new EventEmitter();
        this.relationSearchTextChanged = new EventEmitter();
        this.autocompleteSearchTextChanged = new EventEmitter();
        this.croppedImageUploaded = new EventEmitter();
        this.documentSelected = new EventEmitter();
        this.hasChanges = false;
        /** Validated Value-changes */
        this.changes = [];
        this.editDisabled = false;
        this.invalidFieldsWithValues = [];
        this.showActionButtons = false;
        this.setImageFieldDataSubject = new Subject();
        this.setDocumentFieldDataSubject = new Subject();
        this.forceAllFieldToBeTouched = false;
        this.setTextFieldValueSubject = new Subject();
        this.subscriptions = [];
    }
    ngOnChanges(changes) {
        if (changes.data?.currentValue) {
            this.dataChanged(changes.data.currentValue);
            this.setShowActionButtonsFlag();
        }
        if (changes.acl) {
            this.editDisabled = !this.sharedSvc.hasAccess(this.acl);
            this.setShowActionButtonsFlag();
        }
        if (changes.customSections) {
            this.applyCustomViewSections();
        }
    }
    ngOnInit() {
        if (this.acknowledgeEntity) {
            this.acknowledgeEntitySubscription = this.acknowledgeEntity.subscribe({
                next: entity => this.processAcknowledgeEntity(entity)
            });
            this.subscriptions.push(this.acknowledgeEntitySubscription);
        }
        if (this.changeFieldValue) {
            this.subscriptions.push(this.changeFieldValue.subscribe({
                next: data => this.processChangeFieldValue(data)
            }));
        }
        if (this.setImageFieldData) {
            this.subscriptions.push(this.setImageFieldData.subscribe({
                next: data => this.setImageFieldDataSubject.next(data)
            }));
        }
        if (this.setDocumentFieldData) {
            this.subscriptions.push(this.setDocumentFieldData.subscribe({
                next: data => this.setDocumentFieldDataSubject.next(data)
            }));
        }
        if (this.setExternalError) {
            this.subscriptions.push(this.setExternalError.subscribe({
                next: data => this.processSetExternalError(data)
            }));
        }
        if (this.performSave) {
            this.subscriptions.push(this.performSave.subscribe(() => this.onSave()));
        }
    }
    processSetExternalError(data) {
        this.externalErrorData = data;
        this.setHasError();
    }
    processChangeFieldValue(data) {
        const fieldWithValue = this.grpFldsSvc.getFieldWithValueInGroups(data.fieldId, this.groups);
        if (!fieldWithValue) {
            return;
        }
        fieldWithValue.value = data.value;
        const fieldType = this.sharedSvc.getFieldTypeFromField(fieldWithValue.field);
        const textFieldTypes = ['text', 'virtual-textarea', 'email', 'url'];
        const isFieldTextType = textFieldTypes.includes(fieldType);
        if (isFieldTextType) {
            this.setTextFieldValueSubject.next({ fieldId: fieldWithValue.field.id, value: data.value });
        }
        this.onCellValueChanged(fieldWithValue, {
            field: fieldWithValue.field, value: fieldWithValue.value
        });
    }
    processAcknowledgeEntity(entity) {
        this.data.editModeForAllFields = false;
        if (entity) {
            // Specific entity is sent - use it
            this.data.fieldsWithEntity.entity = entity;
        }
        else {
            // Entity is not sent - use original one and apply changes to it
            const changesOnlyObject = this.grpFldsSvc.createObjectFromChanges(this.changes);
            // object_enum fields which report specific property, must use original value
            // otherwise setting simply string value to entity will break object items of the field
            // Set all object_enum field values in entity "selected" to the item with new reported property
            const objectEnumFieldValues = this.grpFldsSvc.getObjectEnumOriginalValues(changesOnlyObject, this.data.fieldsWithEntity);
            const wholeEntityWithChanges = Object.assign(this.data.fieldsWithEntity.entity, changesOnlyObject);
            this.data.fieldsWithEntity.entity = Object.assign(wholeEntityWithChanges, objectEnumFieldValues);
        }
        this.forceAllFieldToBeTouched = false;
        this.dataChanged(this.data);
        this.changes = [];
        this.setHasChanges();
    }
    dataChanged(data) {
        this.originalFieldsWithEntity = this.sharedSvc.clone(data.fieldsWithEntity);
        this.restoreReferencesAfterClone(this.originalFieldsWithEntity, data.fieldsWithEntity);
        const changedData = data;
        const groupedFields = this.sharedSvc.getGroups(changedData.fieldsWithEntity.fields, x => x.category || '');
        const groups = this.grpFldsSvc.createGroups(groupedFields, changedData.fieldsWithEntity.entity, changedData.fieldsOptions);
        if (!data.disableAutoOrdering) {
            this.grpFldsSvc.sortGroups(groups, data.singleColumn);
        }
        this.calculatedGroups = groups;
        this.groups = groups;
        this.applyCustomViewSections();
    }
    restoreReferencesAfterClone(clonedFieldsWithEntity, fieldsWithEntity) {
        for (const field of fieldsWithEntity.fields) {
            if (field.component === "object_enum" /* FieldComponentType.objectEnum */) {
                const objectEnumValue = clonedFieldsWithEntity.entity[field.name];
                if (objectEnumValue.items && objectEnumValue.selected) {
                    let selectedIndex = objectEnumValue.items.findIndex(x => x === objectEnumValue.selected);
                    if (selectedIndex === -1) {
                        // Not found by reference, try to find it by id
                        selectedIndex = objectEnumValue.items.findIndex(x => x.id === objectEnumValue.selected.id);
                        if (selectedIndex >= 0) {
                            const clonedObjectEnumValue = clonedFieldsWithEntity.entity[field.name];
                            clonedObjectEnumValue.selected = clonedObjectEnumValue.items[selectedIndex];
                        }
                    }
                }
            }
        }
    }
    applyCustomViewSections() {
        if (!this.customSections || this.customSections.length === 0 || this.data.singleColumn) {
            // Single column mode will not use custom sections for now, because custom sections have 2 columns
            this.groups = this.calculatedGroups;
            return;
        }
        const newGroups = [];
        // Create new groups based on the sections and put fields on appropriate positions
        for (const section of this.customSections) {
            const newGroup = {
                key: section.label, items: [], readOnly: section.read_only
            };
            const maxItems = Math.max(section.left_column?.length || 0, section.right_column?.length || 0);
            for (let i = 0; i < maxItems; i++) {
                const leftFieldIdOrName = section.left_column ? section.left_column[i] : null;
                const leftFieldWithValue = this.findFieldWithValueByIdOrName(this.calculatedGroups, leftFieldIdOrName);
                const rightFieldIdOrName = section.right_column ? section.right_column[i] : null;
                const rightFieldWithValue = this.findFieldWithValueByIdOrName(this.calculatedGroups, rightFieldIdOrName);
                newGroup.items.push(leftFieldWithValue || { field: null });
                newGroup.items.push(rightFieldWithValue || { field: null });
            }
            newGroups.push(newGroup);
        }
        this.groups = newGroups;
    }
    findFieldWithValueByIdOrName(groups, fieldIdOrName) {
        for (const group of groups) {
            let field = group.items.find(x => x.field.id === fieldIdOrName);
            if (!field) {
                // Not found by id - try to find it by name
                field = group.items.find(x => x.field.name === fieldIdOrName);
            }
            if (field) {
                return field;
            }
        }
        return null;
    }
    onRelationSearchTextChanged(fieldWithValue, relationSearchTextChangedData) {
        this.relationSearchTextChanged.next({
            fieldWithValue: fieldWithValue, relationSearchTextChangedData: relationSearchTextChangedData
        });
    }
    onAutocompleteSearchTextChanged(fieldWithValue, autocompleteSearchTexChangedData) {
        this.autocompleteSearchTextChanged.next({
            fieldWithValue: fieldWithValue, autocompleteSearchTextChangedData: autocompleteSearchTexChangedData
        });
    }
    onCellValueChanged(item, change) {
        this.grpFldsSvc.addChange(item, change, this.changes);
        this.setHasChanges();
        const obj = this.grpFldsSvc.createObjectFromChanges(this.changes);
        this.removeFromInvalidFieldsWithValues(item);
        this.valuesChanged.next({ changedFieldWithValue: item, changes: this.changes, obj: obj });
    }
    onCellInvalidValueSet(item, change) {
        this.grpFldsSvc.setError(item.field, this.changes);
        this.setHasChanges();
        const obj = this.grpFldsSvc.createObjectFromChanges(this.changes);
        this.setToInvalidFieldsWithValues(item);
        this.valuesChanged.next({
            changedFieldWithValue: item,
            lastInvalidFieldWithValue: item,
            invalidFieldsWithValues: this.invalidFieldsWithValues,
            changes: this.changes,
            obj: obj
        });
        this.cdr.detectChanges();
    }
    setToInvalidFieldsWithValues(item) {
        const index = this.getExistingInvalidFieldWithValueIndex(item);
        if (index === -1) {
            this.invalidFieldsWithValues.push(item);
        }
        else {
            this.invalidFieldsWithValues[index] = item;
        }
        this.setHasError();
    }
    removeFromInvalidFieldsWithValues(item) {
        const index = this.getExistingInvalidFieldWithValueIndex(item);
        if (index === -1) {
            return;
        }
        this.invalidFieldsWithValues.splice(index, 1);
        this.setHasError();
    }
    getExistingInvalidFieldWithValueIndex(item) {
        if (!item || !item.field) {
            return -1;
        }
        const existing = this.invalidFieldsWithValues.findIndex(x => x.field.id === item.field.id);
        return existing;
    }
    onCroppedImageUploaded(item, args) {
        this.croppedImageUploaded.next({ fieldWithValue: item, croppedArgs: args });
    }
    onDocumentSelected(item, args) {
        this.documentSelected.next({ fieldWithValue: item, documentArgs: args });
    }
    onCancel() {
        this.changes = [];
        // Revert back to original data
        this.data.fieldsWithEntity = this.originalFieldsWithEntity;
        this.data.editModeForAllFields = false;
        this.dataChanged(this.data);
        this.setHasChanges();
        this.cancelled.next();
        this.forceAllFieldToBeTouched = false;
        this.invalidFieldsWithValues.splice(0);
        this.setHasError();
        this.cdr.detectChanges();
    }
    async onSave() {
        // Keep the original changes in case forcing all fields to be touched causes the fields to emit changes
        // which will unnecessary modify this.changes
        const originalChanges = this.changes.map(x => ({ ...x }));
        // trigger valueChange on all the fields
        if (this.changes.length < this.data.fieldsWithEntity.fields.length) {
            this.forceAllFieldToBeTouched = true;
            this.data.editModeForAllFields = true;
        }
        // `invalidFieldsWithValues` should only be evaluated when all the child input-field elements finish the valueChange
        await firstValueFrom(this.ngZone.onStable);
        // Revert changes back to the original changes so we avoid emitting fields which were not actually changed by the user
        // but were changed by the automatic field validation above (this.forceAllFieldToBeTouched = true)
        this.changes = originalChanges;
        const isHavingInvalidValues = this.invalidFieldsWithValues?.length > 0;
        if (!isHavingInvalidValues) {
            const obj = this.grpFldsSvc.createObjectFromChanges(this.changes);
            this.saved.next({ changedFieldWithValue: null, changes: this.changes, obj: obj });
        }
        else {
            this.scrollFirstErrorFieldIntoView(this.groups, this.invalidFieldsWithValues, this.groupsContainerElementRef.nativeElement);
        }
        this.setHasChanges();
    }
    scrollFirstErrorFieldIntoView(groups, invalidFieldsWithValues, groupsContainerEl) {
        const allInputFieldEls = Array.from(groupsContainerEl.querySelectorAll('azavista-input-field'));
        const invalidFieldWithValues = [];
        for (const inputFieldEl of allInputFieldEls) {
            const fieldId = inputFieldEl.getAttribute('field-id');
            const invalidField = invalidFieldsWithValues.find(x => x.field.id === fieldId);
            if (invalidField) {
                invalidFieldWithValues.push({ fieldWithValue: invalidField, element: inputFieldEl });
            }
        }
        const orderedGroupedFields = groups.flatMap(x => x.items);
        for (const groupedField of orderedGroupedFields) {
            const invalidFieldWithValue = invalidFieldWithValues.find(x => x.fieldWithValue.field.id === groupedField.field.id);
            if (invalidFieldWithValue) {
                invalidFieldWithValue.element.scrollIntoView({ block: 'center', inline: 'nearest' });
                break;
            }
        }
    }
    switchAllFieldsToEditMode() {
        if (this.editDisabled) {
            return;
        }
        this.data.editModeForAllFields = true;
    }
    setHasChanges() {
        this.hasChanges = (this.changes.length > 0);
        this.setShowActionButtonsFlag();
    }
    setHasError() {
        this.data.hasError = this.externalErrorData?.hasError || this.invalidFieldsWithValues.length > 0;
        this.setShowActionButtonsFlag();
    }
    setShowActionButtonsFlag() {
        if (this.data.showActionButtons === false) {
            this.showActionButtons = false;
            return;
        }
        if (this.editDisabled) {
            this.showActionButtons = false;
            return;
        }
        if (this.data.hasError && this.data.showActionButtons !== true) {
            this.showActionButtons = false;
            return;
        }
        this.showActionButtons = this.data.showActionButtons || this.hasChanges;
    }
    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaGroupedFieldsComponent, deps: [{ token: i1.AzavistaSharedService }, { token: AzavistaGroupedFieldsService }, { token: i0.ChangeDetectorRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.10", type: AzavistaGroupedFieldsComponent, selector: "azavista-grouped-fields", inputs: { data: "data", acknowledgeEntity: "acknowledgeEntity", changeFieldValue: "changeFieldValue", acl: "acl", customSections: "customSections", setImageFieldData: "setImageFieldData", setDocumentFieldData: "setDocumentFieldData", actionButtonsDisabled: "actionButtonsDisabled", progressText: "progressText", fieldAppearance: "fieldAppearance", floatLabel: "floatLabel", setExternalError: "setExternalError", floatActions: "floatActions", performSave: "performSave" }, outputs: { valuesChanged: "valuesChanged", cancelled: "cancelled", saved: "saved", relationSearchTextChanged: "relationSearchTextChanged", autocompleteSearchTextChanged: "autocompleteSearchTextChanged", croppedImageUploaded: "croppedImageUploaded", documentSelected: "documentSelected" }, viewQueries: [{ propertyName: "groupsContainerElementRef", first: true, predicate: ["groupsContainer"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div #groupsContainer class=\"panels-container aza-cmp-flex-column\">\r\n    <azavista-selected-items-count [data]=\"data?.selectedItemsCountData\"></azavista-selected-items-count>\r\n    <mat-expansion-panel *ngFor=\"let group of groups\" [expanded]=\"data.expandPanels\" class=\"mat-elevation-z0\">\r\n        <mat-expansion-panel-header>\r\n            <mat-panel-title class=\"aza-group-title\">{{group.key || ''}}</mat-panel-title>\r\n        </mat-expansion-panel-header>\r\n        <div class=\"aza-cmp-grid\" [class.fields-container]=\"!data.singleColumn\" [class.aza-cmp-full-column-half-row-gap]=\"!data.singleColumn\"\r\n            [class.fields-container-single-column]=\"data.singleColumn\" [class.aza-cmp-half-gap]=\"data.singleColumn\">\r\n            <ng-container *ngFor=\"let item of group.items\">\r\n                <azavista-input-field *ngIf=\"item.field\" [field]=\"item.field\" [value]=\"item.value\"\r\n                    [label]=\"item.field.label\" [showLabelInReadonlyMode]=\"true\" [showLabelForBooleans]=\"true\"\r\n                    [canEdit]=\"!editDisabled && !group.readOnly\"\r\n                    [editMode]=\"data.editModeForAllFields && !group.readOnly\" [fullWidth]=\"true\"\r\n                    [fieldAppearance]=\"fieldAppearance\" [options]=\"item.options\"\r\n                    [setImageFieldData]=\"setImageFieldDataSubject\" [setDocumentFieldData]=\"setDocumentFieldDataSubject\"\r\n                    [setTextFieldValue]=\"setTextFieldValueSubject\"\r\n                    [textAreaOptions]=\"data.textAreaOptions\" (valueChanged)=\"onCellValueChanged(item, $event)\"\r\n                    [floatLabel]=\"floatLabel\"\r\n                    [isTouched]=\"forceAllFieldToBeTouched\"\r\n                    (relationSearchTextChanged)=\"onRelationSearchTextChanged(item, $event)\"\r\n                    (autocompleteSearchTextChanged)=\"onAutocompleteSearchTextChanged(item, $event)\"\r\n                    (invalidValueSet)=\"onCellInvalidValueSet(item, $event)\"\r\n                    (editModeEntered)=\"switchAllFieldsToEditMode()\"\r\n                    (croppedImageUploaded)=\"onCroppedImageUploaded(item, $event)\"\r\n                    (documentSelected)=\"onDocumentSelected(item, $event)\">\r\n                </azavista-input-field>\r\n                <span *ngIf=\"!item.field\"></span>\r\n            </ng-container>\r\n        </div>\r\n    </mat-expansion-panel>\r\n</div>\r\n<div *ngIf=\"invalidFieldsWithValues?.length > 0 || externalErrorData?.hasError\" class=\"error-fields-container aza-cmp-margin\">\r\n    <mat-error>\r\n        <ng-container *ngIf=\"invalidFieldsWithValues?.length > 0\">\r\n            <span class=\"error-field-message\" translate>THE_FOLLOWING_FIELDS_HAVE_ERRORS</span>:\r\n            <span *ngFor=\"let fieldWithValue of invalidFieldsWithValues\"\r\n                class=\"error-field-name\">{{fieldWithValue.field.label}}, </span>\r\n        </ng-container>\r\n        <div *ngIf=\"externalErrorData?.hasError\">\r\n            <span class=\"error-field-message\">{{externalErrorData?.errorText}}</span>\r\n        </div>\r\n    </mat-error>\r\n</div>\r\n<div class=\"progress-text-container aza-cmp-half-margin\">\r\n    <span>{{progressText}}</span>\r\n</div>\r\n<div *ngIf=\"floatActions\" class=\"aza-cmp-triple-margin\"></div>\r\n<div *ngIf=\"showActionButtons\" class=\"action-buttons-container aza-cmp-flex-row aza-cmp-margin\" [class.float-actions]=\"floatActions\">\r\n    <azavista-button (click)=\"onSave()\" [disabled]=\"actionButtonsDisabled\" class=\"save-button\" color=\"primary\">\r\n        <span>{{\"SAVE\" | translate | uppercase}}</span>\r\n    </azavista-button>\r\n    <azavista-button (click)=\"onCancel()\" [disabled]=\"actionButtonsDisabled\" class=\"cancel-button\">\r\n        <span>{{\"CANCEL\" | translate | uppercase}}</span>\r\n    </azavista-button>\r\n</div>\r\n\r\n", styles: [".fields-container{grid-template-columns:1fr 1fr}.fields-container-single-column{grid-template-columns:1fr}.float-actions{position:fixed;bottom:0}.progress-text-container{display:flex;justify-content:flex-end}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i4.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }, { kind: "component", type: i5.MatExpansionPanel, selector: "mat-expansion-panel", inputs: ["disabled", "expanded", "hideToggle", "togglePosition"], outputs: ["opened", "closed", "expandedChange", "afterExpand", "afterCollapse"], exportAs: ["matExpansionPanel"] }, { kind: "component", type: i5.MatExpansionPanelHeader, selector: "mat-expansion-panel-header", inputs: ["tabIndex", "expandedHeight", "collapsedHeight"] }, { kind: "directive", type: i5.MatExpansionPanelTitle, selector: "mat-panel-title" }, { kind: "component", type: i6.AzavistaInputFieldComponent, selector: "azavista-input-field", inputs: ["value", "label", "showLabelForBooleans", "showLabelInReadonlyMode", "field", "locale", "dateTimeDisplayFormat", "dateDisplayFormat", "timeDisplayFormat", "canEdit", "tag", "editMode", "fullWidth", "resetValue", "setImageFieldData", "setDocumentFieldData", "setTextFieldValue", "textAreaOptions", "options", "reportErrorOnInit", "fieldAppearance", "floatLabel", "canBeRequiredAndEmpty", "readonlyMode", "autocompleteAttributeValue", "prefixedValueCssName", "setDefaultValueOnEdit", "alwaysDisplayAsOneLiner", "isTouched", "imgFieldSrc", "layoutDensity"], outputs: ["valueChanged", "invalidValueSet", "relationSearchTextChanged", "autocompleteSearchTextChanged", "editModeEntered", "croppedImageUploaded", "documentSelected"] }, { kind: "directive", type: i7.MatError, selector: "mat-error, [matError]", inputs: ["id"] }, { kind: "component", type: i8.AzavistaButtonComponent, selector: "azavista-button", inputs: ["acl", "disabled", "cssClasses", "containerCssClasses", "buttonAttributes"] }, { kind: "component", type: i9.AzavistaSelectedItemsCountComponent, selector: "azavista-selected-items-count", inputs: ["data"] }, { kind: "pipe", type: i3.UpperCasePipe, name: "uppercase" }, { kind: "pipe", type: i4.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaGroupedFieldsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'azavista-grouped-fields', template: "<div #groupsContainer class=\"panels-container aza-cmp-flex-column\">\r\n    <azavista-selected-items-count [data]=\"data?.selectedItemsCountData\"></azavista-selected-items-count>\r\n    <mat-expansion-panel *ngFor=\"let group of groups\" [expanded]=\"data.expandPanels\" class=\"mat-elevation-z0\">\r\n        <mat-expansion-panel-header>\r\n            <mat-panel-title class=\"aza-group-title\">{{group.key || ''}}</mat-panel-title>\r\n        </mat-expansion-panel-header>\r\n        <div class=\"aza-cmp-grid\" [class.fields-container]=\"!data.singleColumn\" [class.aza-cmp-full-column-half-row-gap]=\"!data.singleColumn\"\r\n            [class.fields-container-single-column]=\"data.singleColumn\" [class.aza-cmp-half-gap]=\"data.singleColumn\">\r\n            <ng-container *ngFor=\"let item of group.items\">\r\n                <azavista-input-field *ngIf=\"item.field\" [field]=\"item.field\" [value]=\"item.value\"\r\n                    [label]=\"item.field.label\" [showLabelInReadonlyMode]=\"true\" [showLabelForBooleans]=\"true\"\r\n                    [canEdit]=\"!editDisabled && !group.readOnly\"\r\n                    [editMode]=\"data.editModeForAllFields && !group.readOnly\" [fullWidth]=\"true\"\r\n                    [fieldAppearance]=\"fieldAppearance\" [options]=\"item.options\"\r\n                    [setImageFieldData]=\"setImageFieldDataSubject\" [setDocumentFieldData]=\"setDocumentFieldDataSubject\"\r\n                    [setTextFieldValue]=\"setTextFieldValueSubject\"\r\n                    [textAreaOptions]=\"data.textAreaOptions\" (valueChanged)=\"onCellValueChanged(item, $event)\"\r\n                    [floatLabel]=\"floatLabel\"\r\n                    [isTouched]=\"forceAllFieldToBeTouched\"\r\n                    (relationSearchTextChanged)=\"onRelationSearchTextChanged(item, $event)\"\r\n                    (autocompleteSearchTextChanged)=\"onAutocompleteSearchTextChanged(item, $event)\"\r\n                    (invalidValueSet)=\"onCellInvalidValueSet(item, $event)\"\r\n                    (editModeEntered)=\"switchAllFieldsToEditMode()\"\r\n                    (croppedImageUploaded)=\"onCroppedImageUploaded(item, $event)\"\r\n                    (documentSelected)=\"onDocumentSelected(item, $event)\">\r\n                </azavista-input-field>\r\n                <span *ngIf=\"!item.field\"></span>\r\n            </ng-container>\r\n        </div>\r\n    </mat-expansion-panel>\r\n</div>\r\n<div *ngIf=\"invalidFieldsWithValues?.length > 0 || externalErrorData?.hasError\" class=\"error-fields-container aza-cmp-margin\">\r\n    <mat-error>\r\n        <ng-container *ngIf=\"invalidFieldsWithValues?.length > 0\">\r\n            <span class=\"error-field-message\" translate>THE_FOLLOWING_FIELDS_HAVE_ERRORS</span>:\r\n            <span *ngFor=\"let fieldWithValue of invalidFieldsWithValues\"\r\n                class=\"error-field-name\">{{fieldWithValue.field.label}}, </span>\r\n        </ng-container>\r\n        <div *ngIf=\"externalErrorData?.hasError\">\r\n            <span class=\"error-field-message\">{{externalErrorData?.errorText}}</span>\r\n        </div>\r\n    </mat-error>\r\n</div>\r\n<div class=\"progress-text-container aza-cmp-half-margin\">\r\n    <span>{{progressText}}</span>\r\n</div>\r\n<div *ngIf=\"floatActions\" class=\"aza-cmp-triple-margin\"></div>\r\n<div *ngIf=\"showActionButtons\" class=\"action-buttons-container aza-cmp-flex-row aza-cmp-margin\" [class.float-actions]=\"floatActions\">\r\n    <azavista-button (click)=\"onSave()\" [disabled]=\"actionButtonsDisabled\" class=\"save-button\" color=\"primary\">\r\n        <span>{{\"SAVE\" | translate | uppercase}}</span>\r\n    </azavista-button>\r\n    <azavista-button (click)=\"onCancel()\" [disabled]=\"actionButtonsDisabled\" class=\"cancel-button\">\r\n        <span>{{\"CANCEL\" | translate | uppercase}}</span>\r\n    </azavista-button>\r\n</div>\r\n\r\n", styles: [".fields-container{grid-template-columns:1fr 1fr}.fields-container-single-column{grid-template-columns:1fr}.float-actions{position:fixed;bottom:0}.progress-text-container{display:flex;justify-content:flex-end}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.AzavistaSharedService }, { type: AzavistaGroupedFieldsService }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }]; }, propDecorators: { data: [{
                type: Input
            }], acknowledgeEntity: [{
                type: Input
            }], changeFieldValue: [{
                type: Input
            }], acl: [{
                type: Input
            }], customSections: [{
                type: Input
            }], setImageFieldData: [{
                type: Input
            }], setDocumentFieldData: [{
                type: Input
            }], actionButtonsDisabled: [{
                type: Input
            }], progressText: [{
                type: Input
            }], fieldAppearance: [{
                type: Input
            }], floatLabel: [{
                type: Input
            }], setExternalError: [{
                type: Input
            }], floatActions: [{
                type: Input
            }], performSave: [{
                type: Input
            }], valuesChanged: [{
                type: Output
            }], cancelled: [{
                type: Output
            }], saved: [{
                type: Output
            }], relationSearchTextChanged: [{
                type: Output
            }], autocompleteSearchTextChanged: [{
                type: Output
            }], croppedImageUploaded: [{
                type: Output
            }], documentSelected: [{
                type: Output
            }], groupsContainerElementRef: [{
                type: ViewChild,
                args: ['groupsContainer', { static: true }]
            }] } });

class AzavistaGroupedFieldsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaGroupedFieldsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.10", ngImport: i0, type: AzavistaGroupedFieldsModule, declarations: [AzavistaGroupedFieldsComponent], imports: [AzavistaSharedModule, MatExpansionModule, AzavistaInputFieldModule, MatDividerModule, AzavistaButtonModule,
            AzavistaSelectedItemsCountModule], exports: [AzavistaGroupedFieldsComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaGroupedFieldsModule, imports: [AzavistaSharedModule, MatExpansionModule, AzavistaInputFieldModule, MatDividerModule, AzavistaButtonModule,
            AzavistaSelectedItemsCountModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaGroupedFieldsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AzavistaGroupedFieldsComponent],
                    imports: [
                        AzavistaSharedModule, MatExpansionModule, AzavistaInputFieldModule, MatDividerModule, AzavistaButtonModule,
                        AzavistaSelectedItemsCountModule
                    ],
                    exports: [AzavistaGroupedFieldsComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AzavistaGroupedFieldsComponent, AzavistaGroupedFieldsModule };
